import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ArrowForward } from '@mui/icons-material';
import { Routes } from '../../../routes';
import { useGetPotStatusSummary } from '../hooks/useGetPotStatusSummary';
import { useGetDashboardPeriodInfo } from '../hooks/useGetDashboardPeriodInfo';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PotStatusSummaryDto } from '../api/dashboardApi';
import {
  getStatusColor,
  getStatusLabel,
  buildFullStatusList,
  filterDisplayStatuses,
} from '../utils/potStatusConfig';
import { formatPeriodSubtitle } from '../utils/periodFormatting';

export function PotStatusOverview() {
  const { data, isLoading } = useGetPotStatusSummary();
  const { data: periodInfo } = useGetDashboardPeriodInfo();

  const periods = periodInfo?.periods ?? [];
  const subtitle = formatPeriodSubtitle(periods);

  const allStatuses = buildFullStatusList(data?.statuses ?? []);
  const chartStatuses = filterDisplayStatuses(data?.statuses ?? []).filter(
    s => s.count > 0
  );
  const totalCount = data?.totalCount ?? 0;

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Card data-testid="pot-status-overview" sx={{ flex: 1, minWidth: 0 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Pot Status Overview
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Chart + Legend */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: { xs: 3, sm: 4 },
          }}
        >
          {/* Donut chart */}
          <Box
            sx={{
              width: 160,
              height: 160,
              position: 'relative',
              flexShrink: 0,
            }}
            data-testid="pot-status-chart"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                {chartStatuses.length > 0 ? (
                  <Pie
                    data={chartStatuses}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={75}
                    strokeWidth={2}
                    stroke="transparent"
                    paddingAngle={1}
                  >
                    {chartStatuses.map(entry => (
                      <Cell
                        key={entry.status}
                        fill={getStatusColor(entry.status)}
                      />
                    ))}
                  </Pie>
                ) : (
                  <Pie
                    data={[{ value: 1 }]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={75}
                    strokeWidth={0}
                  >
                    <Cell fill="rgba(255,255,255,0.08)" />
                  </Pie>
                )}
              </PieChart>
            </ResponsiveContainer>
            {/* Centre label */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
              data-testid="pot-status-total"
            >
              <Typography variant="h5" fontWeight={700} lineHeight={1.1}>
                {totalCount}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.7rem', letterSpacing: '0.02em' }}
              >
                Pots
              </Typography>
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
            <Stack spacing={0.75}>
              {allStatuses.map(entry => (
                <StatusRow key={entry.status} entry={entry} />
              ))}
            </Stack>
          </Box>
        </Box>

        {/* View All Pots link */}
        <Box sx={{ mt: 3 }}>
          <Typography
            component={Link}
            to={Routes.finance.pots}
            variant="body2"
            fontWeight={500}
            sx={{
              color: 'warning.main',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { textDecoration: 'underline' },
            }}
            data-testid="view-all-pots-link"
          >
            View All Pots
            <ArrowForward sx={{ fontSize: 14 }} />
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function StatusRow({ entry }: { entry: PotStatusSummaryDto }) {
  const isEmpty = entry.count === 0;
  const dimmedOpacity = 0.3;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 0.25,
        opacity: isEmpty ? dimmedOpacity : 1,
        transition: 'opacity 0.15s ease',
      }}
      data-testid={`status-row-${entry.status}`}
    >
      {/* Colour dot */}
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: getStatusColor(entry.status),
          flexShrink: 0,
        }}
      />
      {/* Label */}
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {getStatusLabel(entry.status)}
      </Typography>
      {/* Count */}
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 28,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {entry.count}
      </Typography>
      {/* Gross value */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          minWidth: 80,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatCurrency(entry.grossValue)}
      </Typography>
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Card
      data-testid="pot-status-overview-loading"
      sx={{ flex: 1, minWidth: 0 }}
    >
      <CardContent sx={{ p: 3 }}>
        <Skeleton width={180} height={24} sx={{ mb: 0.5 }} />
        <Skeleton width={160} height={18} sx={{ mb: 3 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: { xs: 3, sm: 4 },
          }}
        >
          <Skeleton variant="circular" width={160} height={160} />
          <Stack spacing={0.75} sx={{ flex: 1, width: '100%' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} height={24} />
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
